import { Injectable, NgZone } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class EventStreamService {

  private eventSource: EventSource | null = null;
  private eventsSubject = new Subject<any>();

  private apiUrl: string = `${environment.uriG}`;
  private calendarApiUrl: string = `${this.apiUrl}/api/calendar`;

  constructor(private zone: NgZone) { }

  /**
   * Connect to SSE stream
   * @param userId - The user ID to stream events for
   * @returns Observable that emits event updates
   */
  connectToEventStream(): Observable<any> {
    // Close existing connection if any
    this.disconnect();

    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('[SSE] No authentication token found');
      this.eventsSubject.error(new Error('No authentication token found'));
      return this.eventsSubject.asObservable();
    }

    // Create new EventSource with token as query parameter
    const url = `${this.calendarApiUrl}/events/stream?token=${encodeURIComponent(token)}`;
    console.log('[SSE] Connecting to:', url);
    
    this.eventSource = new EventSource(url);

    // Handle incoming messages
    this.eventSource.onmessage = (event) => {
      this.zone.run(() => {
        try {
          const data = JSON.parse(event.data);
          this.eventsSubject.next(data);
        } catch (error) {
          console.error('[SSE] Failed to parse event data:', error);
          this.eventsSubject.error(error);
        }
      });
    };

    // Handle connection open
    this.eventSource.onopen = () => {
      this.zone.run(() => {
        console.log('[SSE] Connection opened');
      });
    };

    // Handle errors
    this.eventSource.onerror = (error) => {
      this.zone.run(() => {
        console.error('[SSE] Connection error:', error);
        // EventSource will automatically try to reconnect
        // Check the state
        if (this.eventSource?.readyState === EventSource.CONNECTING) {
          console.log('[SSE] Reconnecting...');
        } else if (this.eventSource?.readyState === EventSource.CLOSED) {
          console.log('[SSE] Connection closed');
          this.eventsSubject.error(new Error('SSE connection closed'));
        }
      });
    };

    return this.eventsSubject.asObservable();

  }

  /**
   * Disconnect from SSE stream
   */
  disconnect(): void {
    if (this.eventSource) {
      console.log('[SSE] Closing connection');
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.eventSource !== null && 
           this.eventSource.readyState === EventSource.OPEN;
  }

  /**
   * Get connection state
   */
  getConnectionState(): number {
    return this.eventSource?.readyState ?? EventSource.CLOSED;
  }
}
